import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { ModusIconModule } from '@trimble-gcs/modus';
import { filter, map } from 'rxjs';
import { AppRoute } from '../../app-route';
import { AuthService } from '../../auth/auth.service';
import { AuthState } from '../../auth/auth.state';
import { UserState } from '../../user/user.state';
import { AppBarComponent } from '../app-bar/app-bar.component';
import { tabHostTranslations } from '../tab-host/tab-host.translations';

type RouteKey = `${AppRoute}` | 'loading-demo' | 'dialog-demo' | 'translation-demo';

@Component({
  selector: 'sd-app-host',
  imports: [
    CommonModule,
    RouterModule,
    ModusIconModule,
    AppBarComponent,
    MatSidenavModule,
    MatListModule,
  ],
  templateUrl: './app-host.component.html',
  styleUrls: ['./app-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHostComponent {
  private readonly sidenavToggle = signal(false);

  // We don't need to send strings for this component to the translators,
  // and creating keys without tarnslations would cause the validation task to fail.
  // So, share the strings in common with tab-host and leave the others in English.
  readonly translations = tabHostTranslations();

  readonly loggedIn = this.store.selectSignal(AuthState.loggedIn);
  readonly user = this.store.selectSignal(UserState.user);
  readonly sidenavOpen = computed(() => this.loggedIn() && this.sidenavToggle());
  readonly activeRoute: Signal<RouteKey | undefined> = this.subscribeToActiveRoute();

  constructor(
    private router: Router,
    private store: Store,
    private authService: AuthService,
  ) {
    this.authService.registerTokenExpiryWatch().subscribe();
  }

  toggleSidenav(evt: MouseEvent) {
    if (evt.ctrlKey && evt.shiftKey) {
      this.sidenavToggle.update((value) => !value);
    }
  }

  navigate(path: RouteKey) {
    this.router.navigate([path]);
  }

  logout() {
    this.router.navigate([AppRoute.Logout]);
  }

  private subscribeToActiveRoute() {
    const regex = /\/?([^\/?]+)(\?.*)?/;

    const active$ = this.router.events.pipe(
      filter((evt) => evt instanceof NavigationEnd),
      map((evt) => {
        const url = (evt as NavigationEnd).url;
        return url.match(regex)?.at(1) as RouteKey;
      }),
    );

    return toSignal(active$);
  }
}
